// @file Store for handling Guest ID Card/Modal/Label on the surface
import { getCookie } from '@@/bits/cookie'
import { setOauthToken } from '@@/bits/custom_ajax'
import { captureFetchException } from '@@/bits/error_tracker'
import { isInIframe } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import {
    clearSearchParam,
    getAndClearSearchParam,
    getSearchParam,
    loginWithReferrerUrl,
    signUpWithReferrerUrl,
    transformUrl
} from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { anonymous, displayNameForUser } from '@@/bits/user_model'
import { HttpCode, SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import type { CreateCommentPayload } from '@@/pinia/surface_comments'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { SubmissionRequestRedirectLocation, useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import { useSlideshowRealtimeStore } from '@@/pinia/useSlideshowRealtimeStore'
import type { CreateOrUpdateReactionPayload } from '@@/surface/api/reaction'
import type { Cid, DraftPost, Post, UpdatePostArgs, User } from '@@/types'
import { usePostBuilder } from '@@/vuecomposables/surface_post_builder'
import { configure as configureArvo, ReactionType } from '@padlet/arvo'
import type { ArvoConfig } from '@padlet/universal-post-editor'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

enum ContributionType {
  DraftPost = 'draftPost',
  Comment = 'comment',
  Like = ReactionType.Like,
  Vote = ReactionType.Vote,
  Star = ReactionType.Star,
  Grade = ReactionType.Grade,
  Emoji = ReactionType.Emoji,
}

type ContributionPayload = DraftPost | CreateCommentPayload | CreateOrUpdateReactionPayload

const useSurfaceGuestStore = defineStore('surfaceGuestStore', () => {
  const surfaceStore = useSurfaceStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceUserContributors = useSurfaceUserContributorsStore()
  const surfaceCommentsStore = useCommentsStore()
  const surfaceReactionsStore = useReactionsStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()

  const xGuestIdCard = ref<boolean>(false)
  const xGuestIdModal = ref<boolean>(false)
  const xChangeGuestModal = ref<boolean>(false)
  const isGuestIdLabelExpanded = ref<boolean>(false)
  const userName = ref<string>('')
  const afterSaveNameActions = ref<Array<() => void>>([])
  const userNameErrorMessage = ref<string>('')
  const isAwaitingServerResponse = ref(false)
  const userNameHasViolation = ref<boolean | null>(null)
  const contributionType = ref<ContributionType | null>(null)
  const contributionPayload = ref<ContributionPayload | null>(null)

  // Getters

  const displayName = computed<string>(() => {
    const cookieValue = getCookie('ww_aun')
    let decodedName: string | undefined
    if (cookieValue) {
      try {
        decodedName = decodeURIComponent(cookieValue)
      } catch {
        // Fallback for malformed URI sequences
        decodedName = cookieValue
      }
    }
    // Sanitize name: keep Unicode letters (\p{L}) and combining marks (\p{M}) for all scripts
    // - \p{L}: Latin, Cyrillic, CJK, Korean Hangul, Arabic, Hebrew, Greek, etc.
    // - \p{M}: combining characters for Hindi/Thai/Arabic diacritics, accents, etc.
    const sessionUserName = decodedName?.replace(/[^\p{L}\p{M}]/gu, ' ')
    const name = surfaceStore.user.name !== '' ? (surfaceStore.user.name ?? sessionUserName) : sessionUserName
    return name && name !== '' ? name : anonymous
  })
  const userAvatar = computed<string | undefined>(() => surfaceStore.user.avatar)
  const isUserNameUpdated = computed<boolean>(() => surfaceStore.user.name === displayName.value)
  const shouldEnableAnonymousAttribution = computed<boolean>(() => !surfaceStore.isEmbedded && !isInIframe())
  const isAttributionEnabled = computed<boolean>(
    () => surfaceSettingsStore.isCommentable || surfaceSettingsStore.isReactable,
  )
  const shouldShowGuestIdModal = computed<boolean>(() => {
    return (
      shouldEnableAnonymousAttribution.value &&
      !surfacePermissionsStore.amIRegistered &&
      !isUserNameUpdated.value &&
      isAttributionEnabled.value
    )
  })
  const shouldUpdateNameBeforePublishing = computed<boolean>(() => {
    return (
      shouldEnableAnonymousAttribution.value &&
      !surfacePermissionsStore.amIRegistered &&
      !isUserNameUpdated.value &&
      !isAttributionEnabled.value
    )
  })
  const isUserNameValid = computed((): boolean => {
    if (userNameErrorMessage.value !== '') return false
    if (userNameHasViolation.value === true) return false
    return true
  })
  const hasUserPosted = computed<boolean>(
    () => surfaceUserContributors.postAuthorsById[surfaceStore.user.id] !== undefined,
  )
  const hasUserCommented = computed<boolean>(() => {
    return surfaceCommentsStore.persistedComments.some((comment) => comment.user_id === surfaceStore.user.id)
  })
  const hasUserReacted = computed<boolean>(() => {
    const allReactions = Object.values(surfaceReactionsStore.accumulatedReactionsByWishId).map(
      (reaction) => reaction?.userReaction,
    )
    return allReactions.some((reaction) => reaction?.user_id === surfaceStore.user.id)
  })
  const hasUserContributed = computed<boolean>(
    () => hasUserPosted.value || hasUserCommented.value || hasUserReacted.value,
  )
  const referrerUrl = computed<string>(() => {
    let referrer = surfaceStore.wallAttributes.links?.show != null ? surfaceStore.wallAttributes.links.show : ''

    if (surfaceShareLinksStore.isBreakout && surfaceShareLinksStore.breakoutLinkConfig != null) {
      referrer = surfaceShareLinksStore.breakoutLinkConfig.link
    }

    if (surfaceStore.isSubmissionRequest && surfaceStore.links?.submissionRequest != null) {
      if (surfaceShareLinksStore.submissionRequestRedirectLocation === SubmissionRequestRedirectLocation.Padlet) {
        referrer = surfaceStore.links.show
      } else {
        // For submission request, choosing section is based on query params so we just propagate them.
        const url = new URL(window.location.href)
        referrer = transformUrl(surfaceStore.links.submissionRequest, {
          searchParams: {
            ...Object.fromEntries(url.searchParams),
            shouldPublishDraft:
              surfaceShareLinksStore.submissionRequestRedirectLocation !==
              SubmissionRequestRedirectLocation.Confirmation,
          },
        })
      }
    }

    if (contributionType.value !== null && contributionPayload.value !== null) {
      return transformUrl(referrer, { searchParams: { contribution_type: String(contributionType.value) } })
    }

    return referrer
  })

  const signupUrl = computed<string>(() => {
    return signUpWithReferrerUrl(
      hasUserContributed.value || contributionType.value !== null
        ? transformUrl(referrerUrl.value, { searchParams: { has_user_contributed: 'true' } })
        : referrerUrl.value,
    )
  })

  const loginUrl = computed<string>(() => {
    return loginWithReferrerUrl(referrerUrl.value)
  })
  const contributionPayloadDraftCacheKey = computed<string>(() => {
    return `contributionPayloadDraft-${surfaceStore.wallId}`
  })

  // Actions
  function showGuestIdCard(): void {
    xGuestIdCard.value = true
  }

  function hideGuestIdCard(): void {
    xGuestIdCard.value = false
  }

  function setGuestIdLabelExpanded(expanded: boolean): void {
    isGuestIdLabelExpanded.value = expanded
  }

  function saveContributionPayloadDraft(): void {
    if (contributionType.value == null || contributionPayload.value == null) return

    safeLocalStorage.setItem(
      contributionPayloadDraftCacheKey.value,
      asciiSafeStringify({ [contributionType.value]: contributionPayload.value }),
    )
  }

  function deleteContributionPayloadDraft(): void {
    safeLocalStorage.removeItem(contributionPayloadDraftCacheKey.value)
  }

  function getContributionPayloadDraft(): Record<ContributionType, ContributionPayload> | null {
    const contributionPayloadDraft = safeLocalStorage.getItem(contributionPayloadDraftCacheKey.value)
    if (contributionPayloadDraft == null) return null
    return JSON.parse(contributionPayloadDraft)
  }

  function showGuestIdModal(options?: {
    afterSaveActions?: Array<() => void>
    contributionType?: ContributionType
    contributionPayload?: ContributionPayload
  }): void {
    xGuestIdModal.value = true

    if (options?.contributionType != null && options?.contributionPayload != null) {
      contributionType.value = options.contributionType
      contributionPayload.value = options.contributionPayload
      saveContributionPayloadDraft()
    }

    if (options?.afterSaveActions != null) {
      afterSaveNameActions.value.push(...options?.afterSaveActions)
    }
  }

  function hideGuestIdModal(): void {
    xGuestIdModal.value = false
    afterSaveNameActions.value = []
    deleteContributionPayloadDraft()
  }

  function showChangeGuestModal(): void {
    xChangeGuestModal.value = true
  }

  function hideChangeGuestModal(): void {
    xChangeGuestModal.value = false
  }

  function setUserName(name: string): void {
    userName.value = name
    userNameErrorMessage.value = ''
    userNameHasViolation.value = null
  }

  async function updateSessionUserName(
    { shouldModerate }: { shouldModerate: boolean } = { shouldModerate: true },
  ): Promise<void> {
    const trimmedUserName = userName.value.trim()

    if (trimmedUserName === '' || trimmedUserName === surfaceStore.user.name) return
    try {
      isAwaitingServerResponse.value = true

      if (shouldModerate && trimmedUserName !== displayName.value) {
        await moderateUserName(trimmedUserName)
        if (userNameHasViolation.value !== false) return
      }
      const { PowwowApi } = await import('@@/surface/api/powwow')
      const user = await PowwowApi.updateSessionUserName({
        userId: surfaceStore.user.id,
        name: trimmedUserName,
        wallId: surfaceStore.wallId,
      })
      updateUserInSurfaceStores(user)
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        userNameErrorMessage.value = __('Name invalid')
      } else {
        captureFetchException(e, { source: 'SurfaceGuestStoreUpdateSessionUserName' })
        globalSnackbarStore.setSnackbar({
          message: __('Error updating name'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  function handleNewUserCreated(payload: { user: User; arvoConfig: ArvoConfig }): void {
    updateUserInSurfaceStores(payload.user)
    void configureArvo(payload.arvoConfig as any)
    setOauthToken(
      (
        payload.arvoConfig?.token as {
          oauthToken?: string
          csrfToken?: string | (() => string)
        }
      )?.oauthToken,
    )
    void surfaceStore.refetchStartingStateAndCreateNewBrahmsConnection()
  }

  async function handleLogIn(): Promise<void> {
    const { PowwowApi } = await import('@@/surface/api/powwow')
    const [user, arvoConfig] = await Promise.all([PowwowApi.getCurrentUser(), PowwowApi.getArvoConfig()])
    handleNewUserCreated({ arvoConfig, user })
  }

  async function handleLogOut(): Promise<void> {
    const { PowwowApi } = await import('@@/surface/api/powwow')
    const [user, arvoConfig] = await Promise.all([PowwowApi.getCurrentUser(), PowwowApi.getArvoConfig()])
    surfaceCommentsStore.resetComments()
    surfaceDraftsStore.resetDrafts()
    handleNewUserCreated({ arvoConfig, user })
  }

  async function changeGuest(): Promise<void> {
    const trimmedUserName = userName.value.trim()

    try {
      isAwaitingServerResponse.value = true

      await moderateUserName(trimmedUserName)
      if (userNameHasViolation.value !== false) return

      const { PowwowApi } = await import('@@/surface/api/powwow')
      const { user, arvoConfig } = await PowwowApi.createSessionUser({
        name: trimmedUserName,
      })

      handleNewUserCreated({ user, arvoConfig })

      globalSnackbarStore.setSnackbar({
        message: __('Guest changed to %{userName}', { userName: user.name }),
        notificationType: SnackbarNotificationType.success,
      })
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        userNameErrorMessage.value = __('Name invalid')
      } else {
        captureFetchException(e, { source: 'SurfaceGuestStoreCreateSessionUserName' })
        globalSnackbarStore.setSnackbar({
          message: __('Error changing guest'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  async function moderateUserName(name: string): Promise<void> {
    try {
      const { ContentModerationApi } = await import('@@/surface/api/content_moderation')
      const { userNameHasViolation: hasViolation } = await ContentModerationApi.moderateUserName({
        name,
      })
      userNameHasViolation.value = hasViolation === null ? null : hasViolation

      if (userNameHasViolation.value === true) {
        globalSnackbarStore.setSnackbar({
          message: __('Your name was flagged as inappropriate and was not updated'),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (userNameHasViolation.value === null) {
        globalSnackbarStore.genericFetchError()
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceGuestStoreModerateUserName' })
    }
  }

  function clearContributionPayloadDraft(): void {
    clearSearchParam('contribution_type')
    deleteContributionPayloadDraft()
  }

  function updateUserInSurfaceStores(user: User): void {
    setUserName(user.name as string)
    surfaceUserContributors.updateUser(user)
    surfaceStore.updateUser(user)
    if (surfaceStore.isSlideshow) {
      useSlideshowRealtimeStore().markViewerInfoStale()
    }
  }

  function getDraftPostCid(draftPost: DraftPost): Cid | null {
    const draftCidArray = Object.entries(surfaceDraftsStore.draftByCid).find(
      ([_cid, draft]) => draftPost.id === draft.id,
    )
    return draftCidArray !== undefined ? draftCidArray[0] : null
  }

  async function publishDraftPost(): Promise<void> {
    const contributionType = getSearchParam('contribution_type') as ContributionType
    if (contributionType == null || contributionType !== ContributionType.DraftPost) return

    const contributionPayloadDraft = getContributionPayloadDraft()
    if (contributionPayloadDraft == null || contributionPayloadDraft[contributionType] == null) return
    clearContributionPayloadDraft()
    const contributionPayload = contributionPayloadDraft[contributionType] as DraftPost
    if (contributionPayload.wall_id !== surfaceStore.wallId) return

    let draftPostCid: Cid | null = null

    // All the post drafts are authored by the current user after logging in or signing up
    // So we can just publish the draft stored in the local storage
    draftPostCid = getDraftPostCid(contributionPayload)

    if (draftPostCid == null) return

    try {
      if (surfaceStore.isSubmissionRequest && getSearchParam('shouldPublishDraft') === 'false') {
        clearSearchParam('shouldPublishDraft')
        await surfaceShareLinksStore.startNewSubmissionRequest()
        await surfaceDraftsStore.updateActiveDraft({
          ...contributionPayload,
          cid: draftPostCid,
        })
      } else {
        await surfaceDraftsStore.publishDraft({ cid: draftPostCid })
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceGuestPublishContributionDraft' })
    }
  }

  async function publishCommentOrReactionDraft(): Promise<void> {
    const contributionType = getSearchParam('contribution_type') as ContributionType
    const allowedContributionTypes = [
      ContributionType.Comment,
      ContributionType.Like,
      ContributionType.Vote,
      ContributionType.Star,
      ContributionType.Grade,
      ContributionType.Emoji,
    ]
    if (contributionType == null || !allowedContributionTypes.includes(contributionType)) {
      return
    }
    const contributionPayloadDraft = getContributionPayloadDraft()
    if (contributionPayloadDraft == null || contributionPayloadDraft[contributionType] == null) return
    clearContributionPayloadDraft()
    const contributionPayload = contributionPayloadDraft[contributionType] as
      | CreateCommentPayload
      | CreateOrUpdateReactionPayload

    const post = surfacePostsStore.getPostByServerId(String(contributionPayload.postId))
    if (post == null) return

    try {
      if (contributionType === ContributionType.Comment) {
        await surfaceCommentsStore.createCommentV2(contributionPayload as CreateCommentPayload)
      } else if (surfaceReactionsStore.originalReactionData.type === (contributionType as unknown as ReactionType)) {
        await surfaceReactionsStore.createReaction(contributionPayload as CreateOrUpdateReactionPayload)
      }
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceGuestPublishCommentOrReactionDraft' })
    }
  }

  function showSnackbarAfterSigningUp(): void {
    const hasUserContributed = getAndClearSearchParam('has_user_contributed')
    if (hasUserContributed !== 'true') return

    useGlobalSnackbarStore().setSnackbar({
      notificationType: SnackbarNotificationType.success,
      message: __('All your guest activities have been merged to %{userName}.', {
        userName: displayNameForUser(surfaceStore.user),
      }),
    })
  }

  async function savePostAsGuestIfNecessary({
    attributes,
    updatePostArgs,
  }: {
    attributes: Post
    updatePostArgs?: UpdatePostArgs
  }): Promise<Post | null> {
    if (shouldShowGuestIdModal.value) {
      showGuestIdModal({
        afterSaveActions: [async () => await surfacePostsStore.savePost({ attributes, updatePostArgs })],
        contributionType: ContributionType.DraftPost,
        contributionPayload: attributes as DraftPost,
      })
      return usePostBuilder().buildPost(attributes)
    } else if (shouldUpdateNameBeforePublishing.value) {
      setUserName(displayName.value)
      await updateSessionUserName({ shouldModerate: false })
      return null
    }
    return null
  }

  return {
    // State
    xGuestIdCard,
    xGuestIdModal,
    xChangeGuestModal,
    isGuestIdLabelExpanded,
    userName,
    afterSaveNameActions,
    userNameErrorMessage,
    isAwaitingServerResponse,

    // Getters
    displayName,
    userAvatar,
    isUserNameUpdated,
    shouldEnableAnonymousAttribution,
    isAttributionEnabled,
    shouldShowGuestIdModal,
    shouldUpdateNameBeforePublishing,
    isUserNameValid,
    hasUserPosted,
    hasUserCommented,
    hasUserReacted,
    hasUserContributed,
    signupUrl,
    loginUrl,
    contributionType,
    contributionPayload,

    // Actions
    showGuestIdCard,
    hideGuestIdCard,
    setGuestIdLabelExpanded,
    showGuestIdModal,
    hideGuestIdModal,
    setUserName,
    updateSessionUserName,
    changeGuest,
    showChangeGuestModal,
    hideChangeGuestModal,
    moderateUserName,
    updateUserInSurfaceStores,
    handleNewUserCreated,
    handleLogIn,
    handleLogOut,
    publishDraftPost,
    publishCommentOrReactionDraft,
    showSnackbarAfterSigningUp,
    savePostAsGuestIfNecessary,
  }
})
export { ContributionType, useSurfaceGuestStore }
export type { ContributionPayload }
